import { useQuery } from '@tanstack/react-query';
import { cloudFunctions } from '@/lib/firebase';
import { browserCountry, countryName, toCountryOptions, type CountryOption } from '@/lib/countries';

/** Countries Stripe can open a payout account in, fetched live from Stripe. */
export function useConnectCountries() {
  return useQuery({
    queryKey: ['connectCountries'],
    queryFn: async (): Promise<CountryOption[]> => {
      const { countries } = await cloudFunctions.getConnectCountries({});
      return toCountryOptions(countries);
    },
    staleTime: Infinity,
  });
}

/**
 * The payout country to preselect, in order of evidence: the country of the
 * card they paid Romulus with, then their browser locale. Never a fixed
 * default - if neither is supported, the seller must choose.
 */
export function suggestPayoutCountry(
  countries: CountryOption[],
  billingCountry?: string | null,
): string {
  const supported = (code?: string | null) => !!code && countries.some((c) => c.code === code);
  if (supported(billingCountry)) return billingCountry!;
  const fromBrowser = browserCountry();
  if (supported(fromBrowser)) return fromBrowser!;
  return '';
}

interface PayoutCountryPickerProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (code: string) => void;
  countries: CountryOption[] | undefined;
  loading?: boolean;
}

export function PayoutCountryPicker({ id = 'payoutCountry', label = 'Where do you bank?', value, onChange, countries, loading }: PayoutCountryPickerProps) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-medium text-gray-300">{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading || !countries}
        className="w-full h-11 rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white outline-none focus:border-primary-500/60 disabled:opacity-60"
      >
        <option value="" className="bg-black">
          {loading || !countries ? 'Loading countries…' : 'Select your country'}
        </option>
        {countries?.map((c) => (
          <option key={c.code} value={c.code} className="bg-black">
            {c.flag} {c.name}
          </option>
        ))}
      </select>
      <p className="text-[11px] text-gray-500">
        Pick the country your bank account is in. Stripe locks it once your payouts are live.
        Not listed? Stripe doesn't support payouts there yet.
      </p>
    </div>
  );
}

/** Warning shown when an account that isn't live yet is in a different country from the seller's card. */
export function PayoutCountryMismatch({ accountCountry, billingCountry }: { accountCountry: string; billingCountry: string }) {
  return (
    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-sm text-yellow-200">
      Your payout account is set to {countryName(accountCountry)}, but you paid with a card from{' '}
      {countryName(billingCountry)}. We've selected {countryName(billingCountry)} below. Continuing moves your
      payout account there automatically.
    </div>
  );
}
